import React from 'react';
import { Label } from '@/components/ui/label';
import { formatMoney } from '@/lib/line-items';

export interface PaymentHistoryEntry {
  id: string;
  /** the payment's own visible id ("BP000001" for a bill payment, "IP000001" for an invoice payment) */
  paymentId: string;
  /** the free-text number the user typed on the payment, when there is one */
  reference?: string;
  /** YYYY-MM-DD */
  date?: string;
  amount: number;
}

interface PaymentHistorySectionProps {
  payments: PaymentHistoryEntry[];
  /** the section's label ("Payments" on a bill, "Payments Received" on an invoice) */
  title?: string;
  emptyText?: string;
  testId?: string;
}

/** The rows of a payment table as history entries: only the payments of `parentId` (via `parentField`), a voided payment (not money paid) left out, oldest first. */
export function toPaymentHistoryEntries(rows: any[], parentField: string, parentId: string): PaymentHistoryEntry[] {
  return rows
    .filter((row) => row[parentField] === parentId && row.status !== 'voided')
    .sort((a, b) => String(a.payment_date ?? '').localeCompare(String(b.payment_date ?? '')) || String(a.created_at ?? '').localeCompare(String(b.created_at ?? '')))
    .map((row) => ({
      id: row.id,
      paymentId: row.payment_id || 'Payment',
      reference: row.payment_number || undefined,
      amount: Number(row.amount) || 0,
      date: row.payment_date ? String(row.payment_date).split('T')[0] : '',
    }));
}

/**
 * "Payments" of a document: THE one read-only list of the payments applied to a bill (money paid out) or to an invoice (money received): each one with its id,
 * the number typed on it, the date and the amount. Which payments go in is the caller's choice (bill payments for a bill, invoice payments for an invoice - they are
 * never mixed); only the title and the empty text vary, through props.
 */
export function PaymentHistorySection({ payments, title = 'Payments', emptyText = 'No payments recorded', testId = 'payment-history' }: PaymentHistorySectionProps) {
  return (
    <div className="space-y-2">
      <Label className="font-form-label">{title}</Label>
      <div role="group" aria-label={title} className="rounded-md border border-border p-1" data-testid={testId}>
        {payments.length === 0 && <p className="px-2 py-1.5 text-sm text-gray-500">{emptyText}</p>}
        {payments.map((payment) => (
          <div key={payment.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm">
            <span className="w-24 flex-shrink-0 truncate font-medium" title={payment.paymentId}>{payment.paymentId}</span>
            <span className="min-w-0 flex-1 truncate text-gray-500" title={payment.reference}>{payment.reference}</span>
            <span className="flex-shrink-0 text-xs text-gray-500">{payment.date ? new Date(`${payment.date}T00:00:00`).toLocaleDateString('en-US') : ''}</span>
            <span className="w-24 flex-shrink-0 text-right tabular-nums">{formatMoney(payment.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
